<!--
  DiagTab.vue
  콜봇 월 정산 · 병원 상세 `연결 진단` 탭(?tab=diag) — 화면 명세 2-5 병원 리포트 열람 · 2-6 연결 진단.

  요청: ~/md/front/admin-콜봇-월정산-프론트요청.md "화면 명세 2 › 연결 진단 탭", "요청 계약 6"(재사용)
  데이터
  - 탭을 처음 열 때(마운트) 병원 월간 이용 리포트 GET .../callbot/stats/monthly?month= 를 한 번 부른다
    (원가·금액 필드 없음 — 형태는 ~/md/front/콜봇-월간이용리포트-프론트요청.md "요청 계약"). 2-5·2-6 은 이 응답
    하나로 그린다. 탭은 병원·달마다 한 번 마운트되므로(KeepAlive) 탭을 오갈 때 다시 부르지 않고, 머리의
    새로고침으로만 다시 부른다.
  - 통화 1건 연결 확인(CallConnectionLookup)은 확인할 때마다 통화 단건 GET .../pbx-calls/:voxCallId 를 부른다.
    리포트에 기대지 않아, 리포트를 불러오는 중이거나 실패해도(라우트 없음 404·5xx·시간 초과) 늘 보인다.
    2-5 미응답 목록의 행을 누르면(리포트가 온 뒤) 그 통화로 바로 확인한다.
  - 정산 상세(`detail`)에 기대지 않는다 — 셸은 정산 상세를 기다리지 않고, 상세가 실패하거나 아직 없어도
    (detail=null) 이 탭을 그린다. 이 탭은 detail 이 null 이어도 그대로 동작해야 한다(리포트·통화 단건만 쓴다).
    셸 ↔ 탭 계약(props·emits)은 네 탭이 같다(SettlementDetail.vue).
-->
<template>
  <div class="diag-tab">
    <a-skeleton v-if="loading && !stats" active :paragraph="{ rows: 9 }" />

    <div
      v-else-if="loadError && !stats"
      class="diag-tab__state"
      :class="{ 'is-error': !loadError.unavailable }"
      role="alert"
    >
      <p class="diag-tab__state-text">{{ loadError.text }}</p>
      <p v-if="loadError.unavailable" class="diag-tab__state-hint">
        call-api 의 월간 이용 리포트(GET …/callbot/stats/monthly)가 배포되면 이 탭이 채워집니다.
      </p>
      <a-button :loading="loading" @click="load">다시 시도</a-button>
    </div>

    <template v-else-if="stats">
      <p class="diag-tab__intro">
        <span class="diag-tab__badge">병원 '월간 이용 리포트'와 같은 데이터 · 원가·요금 없음</span>
        병원은 studio 화면에서 금액을 보지 않습니다. 이 병원의 금액은 위 정산 요약과
        <button type="button" class="diag-tab__link" @click="emit('go-tab', 'settle')">정산 탭</button>에서 봅니다.
      </p>

      <a-alert
        v-if="loadError"
        class="diag-tab__stale"
        type="warning"
        show-icon
        :message="`${loadError.text} 아래는 이전에 불러온 리포트입니다.`"
      />

      <DiagReportPanel :stats="stats" :selected-call-id="lookupCallId" @lookup="openLookup">
        <template #header-extra>
          <span v-if="fetchedLabel" class="diag-tab__fetched">{{ fetchedLabel }}</span>
          <a-button size="small" :loading="loading" aria-label="리포트 새로고침" @click="load">새로고침</a-button>
        </template>
      </DiagReportPanel>

      <DiagAttemptsPanel :stats="stats" />
    </template>

    <!-- 통화 1건 연결 확인은 리포트와 따로 부른다 — 리포트를 불러오는 중이거나 실패해도 쓸 수 있다 -->
    <div ref="lookupWrap" class="diag-tab__lookup">
      <CallConnectionLookup ref="lookupRef" :company-id="companyId" :month="month" :api="api" :mock="mock" />
    </div>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import CallConnectionLookup from './CallConnectionLookup.vue';
import DiagAttemptsPanel from './DiagAttemptsPanel.vue';
import DiagReportPanel from './DiagReportPanel.vue';
import { isCallApiUnimplemented, parseCallApiError } from '@/views/Callbot/service/callApiError';
import { formatKstTime } from '../../service/settlementFormat';

const props = defineProps({
  companyId: { type: String, required: true },
  /** 'YYYY-MM'(KST) */
  month: { type: String, required: true },
  /** 정산 상세 — 이 탭은 쓰지 않는다(셸 ↔ 탭 계약이라 받기만 한다). 상세가 실패하거나 아직 없으면 null */
  detail: { type: Object, default: null },
  detailLoading: { type: Boolean, default: false },
  /** settlementApi 또는 같은 모양의 목(getSettlementApi()) */
  api: { type: Object, required: true },
  mock: { type: Boolean, default: false },
  /** 이 탭은 쓰기가 없어 부르지 않는다 */
  reload: { type: Function, required: true },
  /** 이 탭으로 오는 지시는 아직 없다 — 받으면 바로 intent-done */
  intent: { type: Object, default: null },
});

const emit = defineEmits(['go-tab', 'open-reopen', 'intent-done']);

const UNAVAILABLE_TEXT = '병원 월간 이용 리포트 API 가 이 환경에 아직 없습니다.';

const stats = ref(null);
const loading = ref(false);
/** { unavailable: boolean, text: string } */
const loadError = ref(null);
const fetchedLabel = ref('');
let seq = 0;
let disposed = false;

async function load() {
  const my = ++seq;
  loading.value = true;
  loadError.value = null;
  try {
    const res = await props.api.getMonthlyStats(props.companyId, props.month);
    if (disposed || my !== seq) return;
    stats.value = res ?? null;
    fetchedLabel.value = `불러온 시각 ${formatKstTime(Date.now())}`;
  } catch (err) {
    if (disposed || my !== seq) return;
    loadError.value = isCallApiUnimplemented(err)
      ? { unavailable: true, text: UNAVAILABLE_TEXT }
      : { unavailable: false, text: parseCallApiError(err, '병원 월간 이용 리포트를 불러오지 못했습니다.') };
  } finally {
    if (!disposed && my === seq) loading.value = false;
  }
}

onMounted(load);
onBeforeUnmount(() => {
  disposed = true;
});

// ---------------------------------------------------------------------------
// 통화 1건 연결 확인 — 미응답 행을 누르면 그 통화로 확인하고 확인 칸을 보인다
// ---------------------------------------------------------------------------

const lookupRef = ref(null);
const lookupWrap = ref(null);
const lookupCallId = ref('');

function openLookup(voxCallId) {
  if (!voxCallId) return;
  lookupCallId.value = voxCallId;
  void lookupRef.value?.lookup(voxCallId);
  nextTick(() => lookupWrap.value?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' }));
}

// 이 탭이 받을 지시는 아직 없다 — 셸이 지시를 비우게 받기만 한다
watch(
  () => props.intent?.nonce,
  (nonce) => {
    if (nonce !== undefined && nonce !== null) emit('intent-done');
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
@use '../../styles/settlement' as *;

.diag-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 24px;
    border: 1px solid $st-border;
    border-radius: 12px;
    background: #fff;

    &.is-error {
      border-color: $st-danger-border;
    }
  }

  &__state-text {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: $st-text;
  }

  &__state-hint {
    margin: 0;
    font-size: 12.5px;
    color: $st-muted;
  }

  &__intro {
    margin: 0;
    padding: 8px 12px;
    border-radius: 8px;
    background: $st-bg;
    font-size: 12.5px;
    line-height: 1.8;
    color: $st-text-secondary;
  }

  &__badge {
    @include st-pill($st-accent, $st-accent-bg, $st-accent-border);
    margin-right: 6px;
    vertical-align: 1px;
  }

  &__link {
    height: auto; // 전역 button { height: 40px } 덮어쓰기 — 문장 안 링크라 줄 높이를 따른다
    padding: 0;
    border: 0;
    background: none;
    color: $st-accent;
    font-size: inherit;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: 2px solid $st-accent;
      outline-offset: 2px;
    }
  }

  &__stale {
    border-radius: 8px;
  }

  &__fetched {
    font-size: 12px;
    color: $st-muted;
  }

  &__lookup {
    scroll-margin-top: 16px;
  }
}
</style>
